// SCREENSHOT THE PAGE AS THE USER IS SEEING IT — for the "Report issue" button
// (components/ReportIssueModal.jsx). A bug report with a picture of the screen
// is a bug report someone can act on without a back-and-forth; that is the
// whole reason this file exists.
//
// WHY modern-screenshot AND NOT html2canvas. html2canvas rasterises an inline
// <svg> by serialising it into a standalone SVG document — one with no access
// to this document's stylesheet, so no :root custom properties, no inherited
// currentColor. This app paints ~40 inline SVGs (every icon, both chart kinds,
// the choropleth) entirely with `var(--…)`, `currentColor` and Tailwind classes
// that resolve to var(). Measured in Chrome, all of those rasterise to #000000
// through that path: the charts come out as black bars, the map as a black
// blob, every icon as a black smudge. modern-screenshot instead clones the node
// with its COMPUTED styles inlined and hands it to the browser inside a
// <foreignObject>, so the browser resolves the variables itself and the picture
// matches the screen. Same reason the one `color-mix()` in USChoropleth and the
// whole data-theme="dark" palette survive.
import { domToCanvas } from 'modern-screenshot'

// The POST carries the image as base64 inside a JSON body. API Gateway caps a
// request at 10 MB and the Lambda proxy event at 6 MB, and base64 costs a third
// on top — so the encoded string has to stay well under that. This is the
// budget the downgrade ladder below aims at.
export const MAX_ENCODED_BYTES = 3_500_000

// Elements marked with this attribute are left out of the capture. The report
// modal itself is not open yet when the shot is taken, so this is for anything
// that would be noise or a leak in a screenshot — nothing uses it today.
const IGNORE_ATTRIBUTE = 'data-screenshot-ignore'

function pageBackground() {
  // A transparent background composites to black in a PNG. Take the real page
  // colour so a dark-theme screenshot looks like the dark theme.
  for (const el of [document.body, document.documentElement]) {
    const colour = el && getComputedStyle(el).backgroundColor
    if (colour && colour !== 'transparent' && !colour.startsWith('rgba(0, 0, 0, 0)')) {
      return colour
    }
  }
  return '#ffffff'
}

function keep(node) {
  return !(node instanceof Element && node.hasAttribute(IGNORE_ATTRIBUTE))
}

// Two frames, so anything mid-transition when the button was clicked (the
// button's own hover state, a menu closing) has settled before we draw.
//
// Raced against a timer, because Chrome does not run requestAnimationFrame in a
// document that is not being painted. Waiting on it unconditionally is how this
// function hung forever in a hidden tab — and "forever" is not a state a
// complaint button is allowed to reach.
function nextPaint() {
  return new Promise((resolve) => {
    let done = false
    const finish = () => {
      if (!done) {
        done = true
        resolve()
      }
    }
    requestAnimationFrame(() => requestAnimationFrame(finish))
    setTimeout(finish, 250)
  })
}

// Nothing here is allowed to take longer than this. The capture is a
// convenience wrapped around the note, and a note nobody can send because the
// button is still thinking is worse than a note with no picture.
export const CAPTURE_DEADLINE_MS = 8_000

function deadline(promise, ms, message) {
  let timer
  return Promise.race([
    promise.finally(() => clearTimeout(timer)),
    new Promise((_, reject) => {
      timer = setTimeout(() => reject(new Error(message)), ms)
    }),
  ])
}

function shrink(canvas, factor) {
  const small = document.createElement('canvas')
  small.width = Math.max(1, Math.round(canvas.width * factor))
  small.height = Math.max(1, Math.round(canvas.height * factor))
  const ctx = small.getContext('2d')
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(canvas, 0, 0, small.width, small.height)
  return small
}

// PNG is the right format for a screenshot of text, so try it first and only
// trade quality for size when the body would not fit through the gateway.
function encode(canvas) {
  // A zero-sized canvas serialises to the literal string "data:," — six bytes
  // that an <img> renders as nothing and S3 would happily store as a
  // "screenshot". Treat it as a failed capture so the dialog says so, rather
  // than attaching a picture of nothing and calling it evidence.
  if (!canvas.width || !canvas.height) {
    throw new Error('the page rendered as an empty image')
  }

  const png = canvas.toDataURL('image/png')
  if (png.length <= MAX_ENCODED_BYTES) return png

  for (const quality of [0.9, 0.75, 0.6]) {
    const jpeg = canvas.toDataURL('image/jpeg', quality)
    if (jpeg.length <= MAX_ENCODED_BYTES) return jpeg
  }
  // A very tall page on a 4K display. Half the pixels, then accept whatever
  // that gives — still far more useful than no screenshot at all.
  return shrink(canvas, 0.6).toDataURL('image/jpeg', 0.7)
}

/**
 * A PNG (or JPEG, when the PNG is too big) data URL of the current page.
 *
 * Captures what is ON SCREEN: the app is a fixed-height shell whose panes
 * scroll internally, so this is the viewport, and `restoreScrollPosition`
 * makes each scrolled pane render at the scroll offset the user left it at
 * rather than snapped back to its top. Content scrolled out of view is not in
 * the picture — the same deal a phone screenshot gives you.
 *
 * Throws if the capture fails; the caller sends the note without a picture.
 */
export async function captureScreen() {
  await nextPaint()

  const node = document.body
  const canvas = await deadline(
    domToCanvas(node, {
      // 1 device-independent pixel per CSS pixel. On a retina screen scale 2
      // quadruples the bytes for text that is already perfectly legible.
      scale: 1,
      width: Math.min(node.scrollWidth || window.innerWidth, window.innerWidth),
      height: Math.min(node.scrollHeight || window.innerHeight, window.innerHeight),
      backgroundColor: pageBackground(),
      filter: keep,
      // No webfont is loaded anywhere in this app (system stacks only), so the
      // font-embedding pass has nothing to find and only costs a walk over every
      // stylesheet rule.
      font: false,
      features: {
        // Draw scrolled panes where the user actually scrolled them. Off by
        // default, and with it off a chat scrolled to the newest message
        // screenshots as the top of the thread — the wrong screen entirely.
        restoreScrollPosition: true,
      },
      // MEASURED, AND THE DIFFERENCE BETWEEN 3 SECONDS AND 31. Before it clones
      // anything, modern-screenshot waits for every <img> in the subtree, and for
      // an image that is already `complete` it waits on `img.decode()`. In a
      // document whose visibilityState is "hidden" — a background tab, an
      // occluded window, a laptop whose lid just closed — Chrome never runs the
      // decode, so that promise never settles and the capture sits on this
      // timeout instead. With the library default (30 s) the button read
      // "Capturing…" for half a minute; profiled here, the clone itself takes
      // ~50 ms and the whole wait was the two logo <img>s in the sidebar.
      //
      // Three seconds is far longer than a same-origin image needs and short
      // enough that nobody thinks the button is broken. Past it the capture
      // proceeds anyway: the images are already loaded, so they still paint.
      timeout: 3_000,
    }),
    CAPTURE_DEADLINE_MS,
    'the page took too long to draw',
  )

  return encode(canvas)
}
